// ============================================================
// AI Rådgiver — bransjesammenligning: grunnlaget
// ============================================================
// Fanen sammenligner foretaket med andre i samme bransje. Selve bransjeindeksen
// finnes ikke ennå, og denne fila later ikke som noe annet: den bygger grunnlaget
// (bransjekode, størrelsesgruppe, region, år) og sier eksplisitt om det finnes
// sammenligningstall eller ikke.
//
// Hvorfor indeksen ikke bare kan hentes:
//  - Enhetsregisteret gir næringskode, men ingen regnskapstall.
//  - Regnskapsregisteret gir regnskapstall per foretak, men har ingen «median per
//    næringskode»-oppslag. En indeks må bygges ved å hente mange foretak og aggregere.
//  - Regnskapsplikten treffer ikke enkeltpersonforetak under tersklene, så et ENK og
//    de fleste av dets bransjefeller finnes ikke i registeret i det hele tatt.
// `availability` bærer nettopp denne forskjellen, så UI kan si hvorfor.
const PlummeAiAdvisorIndustry = (() => {
  const num = (value, fallback = null) => {
    if (value === null || value === undefined || value === '') return fallback;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  const clean = (value) => String(value || '').trim();
  const safeArray = (value) => (Array.isArray(value) ? value : []);

  // Størrelsesgruppene følger inndelingen bransjestatistikk vanligvis bruker. Gruppen
  // er ikke-overlappende, så et foretak hører alltid til nøyaktig én.
  const SIZE_BANDS = [
    { id: 'micro', max: 4 },
    { id: 'small', max: 19 },
    { id: 'medium', max: 49 },
    { id: 'large', max: Infinity },
  ];
  const sizeBandFor = (employeeCount) => {
    const count = num(employeeCount, 0) || 0;
    return SIZE_BANDS.find((band) => count <= band.max).id;
  };

  // Sammenligningsgrunnlaget er alltid foretak med regnskapsplikt — i praksis
  // aksjeselskaper, siden det er de som leverer årsregnskap til Regnskapsregisteret.
  // Et enkeltpersonforetak får likevel sammenligne seg, men skal få vite hva det
  // måles mot.
  const LIMITED_COMPANY_TYPES = ['AS', 'ASA', 'NUF'];
  const isLimitedCompany = (companyTypeCode) => LIMITED_COMPANY_TYPES.includes(clean(companyTypeCode).toUpperCase());

  // Nøkkeltall som blir skjeve når et enkeltpersonforetak måles mot aksjeselskaper.
  // Årsaken er den samme for alle: eieren i et ENK tar privat uttak i stedet for
  // lønn, så arbeidsinnsatsen ligger ikke i kostnadene. Da ser marginen kunstig høy
  // og kostnadsandelen kunstig lav ut. Vekst og likviditetsgrad rammes ikke, fordi
  // ingen av dem avhenger av hvordan eierens arbeid er ført.
  const SKEWED_AGAINST_LIMITED = ['resultMargin', 'costShare', 'payrollShare', 'operatingMargin'];

  // Indeksen leverer samme nøkkeltall fra to kilder: Regnskapsregisteret dekker bare
  // foretak med regnskapsplikt, SSB dekker alle. Et enkeltpersonforetak skal måles mot
  // hele bransjen når det finnes et slikt tall — da forsvinner skjevheten i stedet for
  // å bare bli advart om.
  const preferredCoverage = (companyTypeCode) => (isLimitedCompany(companyTypeCode) ? 'filers' : 'allEnterprises');
  const BENCH_NAMES = { revenueGrowth: 'Omsetningsvekst', liquidityRatio: 'Likviditetsgrad' };
  const companyValueFor = (metric, financials, bench, employees) => {
    const key = metric.key;
    const income = num(financials?.income);
    if (key === 'costShare') {
      const costs = num(financials?.costs);
      return income > 0 && costs != null ? costs / income * 100 : null;
    }
    if (key === 'resultMargin') return income > 0 ? num(financials?.resultMargin) : null;
    if (key === 'operatingMargin') {
      // SSB reports gross operating result, which excludes some costs in the company report.
      if (metric.source !== 'accountsRegister') return null;
      const costs = num(financials?.costs);
      return income > 0 && costs != null ? (income - costs) / income * 100 : null;
    }
    if (key === 'payrollShare') return income > 0 && num(employees?.payrollCost) > 0 ? num(employees?.payrollShare) : null;
    const row = safeArray(bench).find((entry) => entry.name === BENCH_NAMES[key]);
    return row?.available === true ? num(row.you) : null;
  };
  const comparisonFor = (you, industry, better) => {
    const position = you > industry ? 'above' : you < industry ? 'below' : 'equal';
    return { position, favorable: position === 'equal' ? null : (better === 'high' ? position === 'above' : position === 'below') };
  };
  const pickMetrics = (metrics, preferred) => {
    const byKey = new Map();
    safeArray(metrics).forEach((metric) => {
      const existing = byKey.get(metric.key);
      if (!existing) { byKey.set(metric.key, metric); return; }
      // Foretrukket dekning vinner. Er ingen av dem foretrukket, beholder vi den
      // første — rekkefølgen fra indeksen setter median foran aggregat.
      if (metric.coverage === preferred && existing.coverage !== preferred) byKey.set(metric.key, metric);
    });
    return [...byKey.values()];
  };

  // Hvorfor sammenligningen ikke kan gjøres. Rekkefølgen er den brukeren trenger:
  // mangler bransjekoden, hjelper det ikke at indeksen finnes.
  const resolveAvailability = ({ industryCode, country, index }) => {
    if (country && country !== 'NO') return 'countryUnsupported';
    if (!clean(industryCode)) return 'noIndustryCode';
    if (!index || !Array.isArray(index.metrics) || !index.metrics.length) return 'noIndex';
    return 'available';
  };

  const buildIndustryBasis = ({
    settings = {},
    business = {},
    year,
    employeeCount = null,
    index = null,
    financials = null,
    bench = [],
    employees = null,
  } = {}) => {
    const industryCode = clean(business.industryCode || settings.industryCode);
    const industryLabel = clean(business.industryLabel || settings.industryLabel);
    const country = clean(settings.country || business.country) || 'NO';
    const companyTypeCode = clean(settings.companyTypeCode || business.companyTypeCode);
    const indexAvailability = resolveAvailability({ industryCode, country, index });
    // Ett nøkkeltall per rad, hentet fra den kilden som faktisk dekker foretak som
    // dette. Bare der SSB ikke har noe å tilby står vi igjen med et tall fra
    // regnskapspliktige foretak — og da merkes raden.
    const preferred = preferredCoverage(companyTypeCode);
    // Skjevheten går bare én vei: et enkeltpersonforetak målt mot et utvalg som bare
    // inneholder regnskapspliktige. Et aksjeselskap som måles mot alle foretak er ikke
    // skjevt — det er selv med i grunnlaget.
    const soleTrader = !isLimitedCompany(companyTypeCode);
    const metrics = indexAvailability === 'available'
      ? pickMetrics(index.metrics, preferred).map((metric) => {
        const you = companyValueFor(metric, financials, bench, employees);
        const industry = num(metric.industry);
        if (you == null || industry == null) return null;
        return {
          ...metric, you, industry,
          ...comparisonFor(you, industry, metric.better),
          skewed: soleTrader && metric.coverage === 'filers' && SKEWED_AGAINST_LIMITED.includes(metric.key),
        };
      }).filter(Boolean)
      : [];
    const availability = indexAvailability === 'available' && !metrics.length ? 'noCompanyData' : indexAvailability;
    // Står det fortsatt igjen skjeve rader, er påminnelsen relevant. Er alle dekket av
    // SSB, er den bare støy.
    const crossForm = metrics.some((metric) => metric.skewed);
    return {
      year,
      industryCode: industryCode || null,
      industryLabel: industryLabel || null,
      sizeBand: sizeBandFor(employeeCount),
      employeeCount: num(employeeCount, 0) || 0,
      region: clean(settings.postalPlace || business.postalPlace) || null,
      country,
      companyTypeCode: companyTypeCode || null,
      availability,
      // Hva sammenligningen faktisk måles mot. For et enkeltpersonforetak er det hele
      // bransjen der SSB har tallet, ikke bare de regnskapspliktige.
      peerBasis: preferred === 'allEnterprises' ? 'allEnterprises' : 'limitedCompanies',
      crossForm,
      // Fylles av indeksen når den finnes. Til da er den bevisst tom i stedet for å
      // være fylt med foretakets egne tall under et bransjenavn.
      metrics,
      updatedAt: availability === 'available' ? index.updatedAt || null : null,
      companiesInGroup: indexAvailability === 'available'
        ? (() => {
          const count = num(preferred === 'filers' ? index.accountsSampleSize : index.companiesInGroup);
          return count > 0 ? count : null;
        })()
        : null,
    };
  };

  return { buildIndustryBasis, sizeBandFor, isLimitedCompany, resolveAvailability, pickMetrics, preferredCoverage, comparisonFor, SIZE_BANDS, SKEWED_AGAINST_LIMITED };
})();

if (typeof globalThis !== 'undefined') globalThis.PlummeAiAdvisorIndustry = PlummeAiAdvisorIndustry;
if (typeof module !== 'undefined' && module.exports) module.exports = PlummeAiAdvisorIndustry;
